import {
  Avatar,
  Button as MuiButton,
  ClickAwayListener,
  styled,
  Typography,
} from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { useCallback, useEffect, useRef, useState } from 'react'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { Button } from '../../../../components/buttons'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { SelectOption } from '../../../../model/selectOption'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { intl } from '../../../../../i18n'
import { ProjectMemberSearchOption } from '../../../../../domain/value-object/ProjectMemberSearchOption'
import { useProjectMemberRepository } from '../../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { ProjectMemberCheckBoxPopper } from './ProjectMemberCheckBoxPopper'
import {
  ProjectMemberPopper,
  ProjectMemberPopperHandle,
} from '../../../../features/ProjectMemberPopper'

const Container = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
  height: '100%',
  outline: 'none',
})

const TitleTypography = styled(Typography)({
  color: colorPalette.monotone[4],
  minWidth: '80px',
  marginRight: '10px',
})

const PullDownSpan = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  maxWidth: '416px',
  flexWrap: 'wrap',
})

const SelectedValueBox = styled('div')({
  display: 'flex',
  padding: '3px 5px',
  borderRadius: '15px',
  margin: '3px 5px',
  flexWrap: 'wrap',
  background: colorPalette.monotone[1],
})

const SelectedAvatar = styled(Avatar)({
  width: 20,
  height: 20,
  margin: 'auto 5px auto 5px',
})

const SelectedNameTypography = styled(Typography)({
  margin: 'auto 5px',
  color: colorPalette.monotone[5],
})

const ClearButton = styled(Button)({
  border: 'none',
  background: colorPalette.monotone[0],
  width: '10px',
  height: '24px',
  borderRadius: '15px',
  cursor: 'pointer',
})

const StyledClearIcon = styled(ClearRoundedIcon)({
  color: colorPalette.monotone[4],
  height: '13px',
  width: '13px',
})

const AddButton = styled(Button)({
  borderRadius: '15px',
  border: `1px dashed ${colorPalette.monotone[2]}`,
  background: colorPalette.monotone[0],
  color: colorPalette.monotone[3],
  margin: '4px auto 4px 15px',
  cursor: 'pointer',
})

const StyledAddIcon = styled(AddRoundedIcon)({
  height: '12px',
  width: '12px',
})

const ExpandSpan = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  maxWidth: '450px',
  margin: 'auto 0 auto auto',
})

const ExpandButton = styled(MuiButton)({
  minWidth: '20px',
  margin: '0 0 0 auto',
})

const StyledExpandLess = styled(ExpandLess)({
  color: colorPalette.monotone[4],
})

const StyledExpandMore = styled(ExpandMore)({
  color: colorPalette.monotone[4],
})

const ClearAllButton = styled(MuiButton)({
  minWidth: '20px',
  margin: '3px auto 3px 0',
})

const ClearAllIcon = styled(ClearRoundedIcon)({
  color: colorPalette.monotone[4],
})

type ProjectMemberSearchConditionSelectBoxProps = {
  value: string[] | undefined
  fetchSelectedOption: (value: string) => Promise<SelectOption | undefined>
  title: string
  onChange: (newValue: string[] | undefined) => void
  singleSelect?: boolean
  memberOptions?: ProjectMemberSearchOption[]
}

export const ProjectMemberSearchConditionSelectBox = ({
  value,
  fetchSelectedOption,
  title,
  onChange,
  singleSelect = false,
  memberOptions: externalMemberOptions,
}: ProjectMemberSearchConditionSelectBoxProps) => {
  const [isPopperOpen, setIsPopperOpen] = useState(false)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const popperRef = useRef<ProjectMemberPopperHandle>(null)

  const [internalMemberOptions, setInternalMemberOptions] = useState<
    ProjectMemberSearchOption[]
  >([])
  const projectMemberRepository = useProjectMemberRepository()

  useEffect(() => {
    if (externalMemberOptions || !isPopperOpen) return
    const searchMembers = async () => {
      const result =
        await projectMemberRepository.searchAsProjectMemberSearchOption('')
      setInternalMemberOptions(result)
    }
    searchMembers()
  }, [externalMemberOptions, isPopperOpen, projectMemberRepository])

  const memberOptions = externalMemberOptions ?? internalMemberOptions

  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([])
  useEffect(() => {
    if (!value || value.length === 0) {
      setSelectedOptions([])
      return
    }
    Promise.all(value.map(v => fetchSelectedOption(v))).then(options => {
      setSelectedOptions(
        options.filter((o): o is SelectOption => o !== undefined)
      )
    })
  }, [value, fetchSelectedOption])

  const closePopper = useCallback(() => {
    setIsPopperOpen(false)
  }, [])

  const togglePopper = useCallback(async () => {
    if (isPopperOpen) {
      setIsPopperOpen(false)
    } else {
      if (!externalMemberOptions) {
        const result =
          await projectMemberRepository.searchAsProjectMemberSearchOption('')
        setInternalMemberOptions(result)
      }
      setIsPopperOpen(true)
      boxRef.current?.focus()
    }
  }, [isPopperOpen, externalMemberOptions, projectMemberRepository])

  const clearAll = useCallback(() => {
    onChange(undefined)
  }, [onChange])

  const removeItem = useCallback(
    (uuid: string) => {
      if (!value) return
      const newValue = value.filter(v => v !== uuid)
      onChange(newValue.length > 0 ? newValue : undefined)
    },
    [value, onChange]
  )

  const addItem = useCallback(
    (uuid: string) => {
      if (value?.includes(uuid)) return
      onChange([...(value || []), uuid])
    },
    [value, onChange]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!singleSelect || !isPopperOpen) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        popperRef.current?.arrowDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        popperRef.current?.arrowUp()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const selected = popperRef.current?.enter()
        if (selected) {
          setIsPopperOpen(false)
        }
      }
    },
    [singleSelect, isPopperOpen]
  )

  return (
    <Container
      ref={boxRef}
      tabIndex={singleSelect ? 0 : undefined}
      onKeyDown={singleSelect ? handleKeyDown : undefined}
    >
      <TitleTypography>{title}</TitleTypography>
      <ClickAwayListener onClickAway={closePopper}>
        <span style={{ display: 'contents' }}>
          <PullDownSpan ref={anchorRef}>
            {value && value.length >= 1 ? (
              <>
                {selectedOptions.map(option => (
                  <SelectedValueBox key={option.value}>
                    <SelectedAvatar
                      variant="circular"
                      src={option.iconUrl || undefined}
                    />
                    <SelectedNameTypography>
                      {option.name || null}
                    </SelectedNameTypography>
                    <ClearButton
                      colorPattern="monotone"
                      onClick={() => removeItem(option.value)}
                    >
                      <StyledClearIcon />
                    </ClearButton>
                  </SelectedValueBox>
                ))}
                <ClearAllButton onClick={clearAll}>
                  <ClearAllIcon />
                </ClearAllButton>
              </>
            ) : (
              <AddButton
                colorPattern="monotone"
                size="s"
                onClick={togglePopper}
              >
                <Typography>
                  {intl.formatMessage({
                    id: 'wbsItemSearchFilterPanel.click.select',
                  })}
                </Typography>
                <StyledAddIcon />
              </AddButton>
            )}
            {singleSelect ? (
              isPopperOpen && (
                <ProjectMemberPopper
                  ref={popperRef}
                  open={isPopperOpen}
                  anchorEl={anchorRef.current}
                  options={memberOptions}
                  onSelect={(member: ProjectMemberSearchOption) => {
                    addItem(member.uuid)
                    setIsPopperOpen(false)
                  }}
                  inputRef={boxRef as React.RefObject<HTMLInputElement>}
                  searchText=""
                />
              )
            ) : (
              <ProjectMemberCheckBoxPopper
                open={isPopperOpen}
                anchorEl={anchorRef.current ?? undefined}
                options={memberOptions}
                value={value}
                onChange={onChange}
              />
            )}
          </PullDownSpan>
          <ExpandSpan>
            <ExpandButton onClick={togglePopper}>
              {isPopperOpen ? <StyledExpandLess /> : <StyledExpandMore />}
            </ExpandButton>
          </ExpandSpan>
        </span>
      </ClickAwayListener>
    </Container>
  )
}
